/**
 * Created by Alan Yang on 3/07/2019.
 * Description:
 * Flat File management component
 * ------ maintenance history ------
 * Updated by Ella Ma on 09/05/2019 upgrade grid
 * 2021-7-29 Simon Zhao retrieve the provider type from the bz constants.
 * Updated by Daniel Wang on 04/18/2022, Added last edited date on flatfile.
 */
import { ColDef, GridOptions } from 'ag-grid-community';
import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from '../../redux';
import { SystemUser } from '../../tamalelibs/models/user.model';
import { FlatFileActionsComponent } from './flat-file-actions.component';
import { FlatFileActionButtonComponent } from './flat-file-action-button.component';
import { FlatFileClickableButtonComponent } from './flat-file-clickable-button.component';
import { FlatFileActionService } from './upload-flat-file-dialog.service';
import { StoreQuerierService } from '../../services/store-querier.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { dataproviderSelector } from '../../redux/reducers/dataprovider.reducer';
import { Dataprovider } from '../../tamalelibs/models/dataprovider.model';
import { businessConstants } from '../../constants/business.constants';
import { DatePipe } from '@angular/common';

export const DEFAULT_PAGE_SIZE = 9999;

@Component({
    selector: 'tam-flat-file-management',
    templateUrl: './flat-file-management.component.html',
    styleUrls: ['./flat-file-management.component.scss']
})
export class FlatFileManagementComponent implements OnInit, OnDestroy {

    columnDefs: Array<ColDef>;
    gridOptions: GridOptions;
    loadingMore = false;
    reloading = false;
    selectedItemMap: Map<string, number> = new Map();
    noDataStyle = 'none';
    rowData: Array<any> = [];
    isPPCUser = false;
    dataManagementConfig: ContextMenuConfig;

    private _currentUser: SystemUser;
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi;
    private readonly _dataColumnType = businessConstants.dataSourceTypeMap;

    constructor(
        private _datepipe: DatePipe,
        private _flatFileActionService: FlatFileActionService,
        private _router: Router,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
    ) {
    }

    ngOnInit() {
        this._currentUser = this._storeQuerier.getCurrentUser();
        this.isPPCUser = this._currentUser.credential.publishPubConfiguration;

        this._destroySubscriptions.push(
            this._store.pipe(
                select(dataproviderSelector),
            ).subscribe((res: Array<Dataprovider>) => {
                this._onFlatFileLoaded(res);
            })
        );
        this._initImportDataDropdown();

        // update the source column -- the csv file of a row in this page.
        this._flatFileActionService.updateItem$.subscribe((selectedRowData) => {
            this._updateItem(selectedRowData);
        });

        this._setAgGridColumns();
        this._setAgGridOptions();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    /**
     * Go to import data from flat file page
     *
     * @memberof FlatFileManagementComponent
     */
    btnDataImportOnClick() {
        this._router.navigate(['dataimport'], {
            queryParams: {
                type: 'flatfile',
                action: 'new',
            },
            skipLocationChange: true,
        });
    }

    /**
     * Go to import data from SQL MDL page
     *
     * @memberof FlatFileManagementComponent
     */
    btnDataImportSQLMDLOnClick() {
        this._router.navigate(['dataimport'], {
            queryParams: {
                type: 'sqlmdl',
                action: 'new',
            },
            skipLocationChange: true,
        });
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    onGridReady(params) {
        this._gridApi = params.api;
    }

    onImportDataItemSelect(event) {
        if (event && event.payload) {
            this._router.navigate(['dataimport'], {
                queryParams: {
                    type: event.payload.id,  // 1 flat file; 2 sql mdl; 3 ODBC mdl
                    action: 'new',
                },
                skipLocationChange: true,
            });
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    onRowSelected(event) {
        const item = event.api.getSelectedNodes()[0];
        this.selectedItemMap.clear();
        this.selectedItemMap.set(item.data.id, item.rowIndex);
    }

    showContextMenu(e: MouseEvent) {
        this.dataManagementConfig.show$.next(new ContextMenuShowAction(e, ''));
    }

    private _getDisplayDateFormat(value: string): string {
        let returnValue = '';
        if (value) {
            returnValue = this._datepipe.transform(value, 'MM-dd-yyyy HH:mm');
        }
        return returnValue;
    }

    private _initImportDataDropdown() {
        this.dataManagementConfig = new ContextMenuConfig();
        this.dataManagementConfig.stateful = false;
        this.dataManagementConfig.showItemIcon = false;

        this.dataManagementConfig.items = Object.keys(this._dataColumnType).map(k => ({ id: k, text: this._dataColumnType[k] }));
        this._destroySubscriptions.push(
            this.dataManagementConfig.events$.subscribe(event =>
                this.onImportDataItemSelect(event)),
        );
    }

    private _onFlatFileLoaded(data: Array<Dataprovider>) {
        this.rowData = data;
    }

    private _setAgGridOptions() {
        this.gridOptions = {
            debug: false,
            defaultColDef: {
                sortable: true,
                resizable: true,
                menuTabs: []
            },
            suppressAutoSize: false,
            cacheBlockSize: 50,
            colResizeDefault: 'shift',
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            suppressColumnVirtualisation: true,
            paginationPageSize: DEFAULT_PAGE_SIZE,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
            getRowHeight: () => 40
        };
    }

    // Defining data for columns
    private _setAgGridColumns() {
        const cellStyleHandler = (params) => {
            return {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none'
            };
        };
        this.columnDefs = [
            {
                headerName: 'Name',
                valueGetter: 'data.name',
                colId: 'name',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                comparator: function (a, b) {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                width: 96,
                field: 'data.name',
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Description',
                valueGetter: 'data.description',
                colId: 'description',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                width: 336,
                comparator: function (a, b) {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },

                field: 'data.description',
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Type',
                valueGetter: 'data.type',
                colId: 'type',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                minWidth: 48,
                field: 'data.type',
                cellRenderer: (params) => {
                    return this._dataColumnType[params.value] || params.value;
                },
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Source',
                valueGetter: (params) => {
                    if (params.data.serverName) {
                        return params.data.serverName;
                    } else {
                        return params.data.fileName;
                    }
                },
                colId: 'source',
                tooltipValueGetter: function (params) {
                    return (params.value);
                },
                comparator: function (a, b) {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                width: 144,
                cellRenderer: FlatFileClickableButtonComponent
            },
            {
                headerName: 'Provider Column',
                valueGetter: 'data.providerColumn',
                colId: 'providerColumn',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                comparator: function (a, b) {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                width: 96,
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Entity Reference',
                valueGetter: 'data.entityReference',
                colId: 'entityReference',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                comparator: function (a, b) {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                width: 96,
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Alias',
                valueGetter: 'data.aliasPrefix',
                colId: 'aliasPrefix',
                tooltipValueGetter: (params) => {
                    return (params.value);
                },
                comparator: function (a, b) {
                    if (typeof a === 'string') {
                        return a.localeCompare(b);
                    } else {
                        return (a > b ? 1 : (a < b ? -1 : 0));
                    }
                },
                width: 96,
                cellStyle: cellStyleHandler
            },
            {
                headerName: 'Last Edited Date',
                valueGetter: 'data.lastEditedDate',
                colId: 'lastEditedDate',
                cellRenderer: (data) => {
                    return this._getDisplayDateFormat(data.value);
                },
                tooltipValueGetter: (params) => {
                    return this._getDisplayDateFormat(params.value);
                },
                width: 96,
                cellStyle: cellStyleHandler
            },
            {
                headerName: '',
                minWidth: 48,
                width: 48,
                maxWidth: 48,
                cellStyle: (params) => {
                    // only show on flatfile.
                    if (params.data.type !== 1) {
                        return { 'display': 'none' };
                    }
                },
                cellRenderer: FlatFileActionsComponent,
                sortable: false,
            },
            {
                headerName: 'Edit/Delete',
                minWidth: 96,
                width: 96,
                maxWidth: 96,
                cellRenderer: FlatFileActionButtonComponent,
                sortable: false,
                suppressMovable: true,
                headerClass: 'header-show',
                cellStyle: { 'text-align': 'center' },
            },
            {
                hide: true,
                headerName: 'Type',
                valueGetter: 'data.type',
                colId: 'type',
            },
            {
                hide: true,
                headerName: '',
                valueGetter: 'data.createDate',
                sort: 'desc'
            }
        ];
    }

    private _updateItem(selectedRowData) {
        const index = this.rowData.indexOf(this.rowData.find(data => (data.id === selectedRowData.id)));
        this.rowData[index].filename = selectedRowData.filename;

        if (this._gridApi) {
            this._gridApi.setRowData(this.rowData);
            this._gridApi.sizeColumnsToFit();
        }
    }
}
